import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NdfCalculatorFilterRequest } from '@app/core/models/ndf-calculator-filter/ndf-calculator-filter-request';
import { VaultTranslatedBaseComponent } from '@app/shared/components/base/vault-translated-base.component';
import { ApiResponse } from '@interfaces/common/apiResponse';
import { Company } from '@interfaces/common/company';
import { InternationalCurrency } from '@interfaces/common/internationalCurrencies';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { NdfCalculatorService } from '@services/http/ndf-calculator.service';
import { CognitoRxjsOperator } from '@src/app/core/rxjs-operators/cognito-rxjs-operator';
import { ActiveFilterConfig } from '@src/app/shared/interfaces/active-filter.interface';
import { Observable, map, of } from 'rxjs';

@Component({
  selector: 'app-ndf-calculator-filter',
  templateUrl: './ndf-calculator-filter.component.html',
  styleUrls: ['./ndf-calculator-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NdfCalculatorFilterComponent
  extends VaultTranslatedBaseComponent
  implements OnInit, OnChanges
{
  @Input() companyId!: string;
  @Input() currentFilters: NdfCalculatorFilterRequest | null = null;
  @Input() isActive: boolean = false;

  @Output() sendCloseFilter = new EventEmitter<boolean>();
  @Output() sendFilter = new EventEmitter<NdfCalculatorFilterRequest>();
  @Output() sendActiveFilters = new EventEmitter<ActiveFilterConfig[]>();

  filterForm!: FormGroup;

  // Opções estáticas
  operationOptions: Option[] = [
    { label: this.t(this.tk.Management.NdfImport), value: 'Import' },
    { label: this.t(this.tk.Management.NdfExport), value: 'Export' },
    { label: this.t(this.tk.Management.NdfLoan), value: 'Loan4131' },
    { label: this.t(this.tk.Management.Indicative), value: 'Indicative' },
  ];

  // Observables para dados dinâmicos
  currencies$!: Observable<Option[]>;
  companies$!: Observable<Option[]>;

  // Cache para resolução síncrona
  private currenciesCache: Option[] = [];
  private companiesCache: Option[] = [];

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly formBuilder: FormBuilder,
    private readonly cognitoRxjsOperator: CognitoRxjsOperator,
    private readonly ndfCalculatorService: NdfCalculatorService,
    translateService: TranslateService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadCurrencies();
    // Não precisa mais carregar empresas manualmente - app-select-company faz isso
    this.populateFormWithCurrentFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentFilters']) {
      if (this.filterForm) {
        this.populateFormWithCurrentFilters();
      }
      this.emitActiveFilters();
    }
  }

  private async initializeForm(): Promise<void> {
    this.filterForm = this.formBuilder.group({
      internationalCurrencyId: [''],
      companyId: [''],
      dateFrom: [''],
      dateTo: [''],
      reference: [''],
      notionalAmountFrom: [0],
      notionalAmountTo: [],
      operationType: [''],
      withHiredOperations: [false],
      withHiredOperationsOpen: [false],
    });
  }

  private loadCurrencies(): void {
    this.currencies$ = this.ndfCalculatorService.getInternationalCurrencies().pipe(
      this.cognitoRxjsOperator.httpHandler(this.destroyRef, {
        showLoading: false,
      }),
      map((currencies: InternationalCurrency[] | null) =>
        (currencies || []).map((currency: InternationalCurrency) => ({
          label: currency.currencyCode,
          value: currency.id,
        }))
      )
    );

    this.currencies$.subscribe(options => {
      this.currenciesCache = options;
      this.emitActiveFilters();
    });
  }

  private loadCompanies(): void {
    // Não precisa mais carregar empresas manualmente - app-select-company faz isso
    this.companies$ = of([]);
    this.companiesCache = [];
  }

  private populateFormWithCurrentFilters(): void {
    if (!this.filterForm) return;

    // Se currentFilters é null, limpar o formulário
    if (!this.currentFilters) {
      this.filterForm.reset();
      this.filterForm.patchValue({
        notionalAmountFrom: '',
        notionalAmountTo: '',
        withHiredOperations: false,
        withHiredOperationsOpen: false,
      });
      return;
    }

    // Atualizar com os valores atuais dos filtros
    this.filterForm.patchValue({
      internationalCurrencyId: this.currentFilters.internationalCurrencyId || '',
      companyId: this.currentFilters.companyId || '',
      dateFrom: this.currentFilters.dateFrom || '',
      dateTo: this.currentFilters.dateTo || '',
      reference: this.currentFilters.reference || '',
      notionalAmountFrom: this.currentFilters.notionalAmountFrom || 0,
      notionalAmountTo: this.currentFilters.notionalAmountTo,
      operationType: this.currentFilters.operationType || '',
      withHiredOperations: this.currentFilters.withHiredOperations || false,
      withHiredOperationsOpen: this.currentFilters.withHiredOperationsOpen || false,
    });

    this.emitActiveFilters();
  }

  clear(): void {
    this.filterForm.reset();
    this.filterForm.patchValue({
      notionalAmountFrom: '',
      notionalAmountTo: '',
      withHiredOperations: false,
      withHiredOperationsOpen: false,
    });
    this.submit();
  }

  submit(): void {
    const formValue = this.filterForm.value;

    // Validar range de valores
    if (
      formValue.notionalAmountFrom &&
      formValue.notionalAmountTo &&
      formValue.notionalAmountFrom > formValue.notionalAmountTo
    ) {
      [formValue.notionalAmountFrom, formValue.notionalAmountTo] = [
        formValue.notionalAmountTo,
        formValue.notionalAmountFrom,
      ];
    }

    // Validar range de datas
    if (
      formValue.dateFrom &&
      formValue.dateTo &&
      new Date(formValue.dateFrom) > new Date(formValue.dateTo)
    ) {
      [formValue.dateFrom, formValue.dateTo] = [formValue.dateTo, formValue.dateFrom];
    }

    const filter: NdfCalculatorFilterRequest = {
      internationalCurrencyId: formValue.internationalCurrencyId,
      companyId: formValue.companyId,
      dateFrom: formValue.dateFrom,
      dateTo: formValue.dateTo,
      reference: formValue.reference,
      notionalAmountFrom: formValue.notionalAmountFrom?.toString(),
      notionalAmountTo: formValue.notionalAmountTo?.toString(),
      operationType: formValue.operationType,
      withHiredOperations: formValue.withHiredOperations,
      withHiredOperationsOpen: formValue.withHiredOperationsOpen,
    };

    // Remove campos vazios
    Object.keys(filter).forEach(key => {
      const value = filter[key as keyof NdfCalculatorFilterRequest];
      if (
        value === '' ||
        value === null ||
        value === undefined ||
        (typeof value === 'boolean' && !value)
      ) {
        delete filter[key as keyof NdfCalculatorFilterRequest];
      }
    });

    this.sendFilter.emit(filter);
    this.sendCloseFilter.emit(false);
    this.emitActiveFilters();
  }

  onSidebarHide(): void {
    this.sendCloseFilter.emit(false);
  }

  // Métodos para compatibilidade
  closeFilter(): void {
    this.sendCloseFilter.emit(false);
  }

  resetFilter(): void {
    this.clear();
  }

  filter(): void {
    this.submit();
  }

  /**
   * Gera e emite as configurações dos filtros ativos
   */
  private emitActiveFilters(): void {
    const activeFilterConfigs: ActiveFilterConfig[] = [];

    if (this.currentFilters) {
      // Moeda
      if (this.currentFilters.internationalCurrencyId) {
        const currencyLabel = this.getCurrencyLabelById(
          this.currentFilters.internationalCurrencyId
        );
        activeFilterConfigs.push({
          propertyName: 'internationalCurrencyId',
          label: 'internationalCurrencyId',
          type: 'text',
          value: currencyLabel,
          group: 'Moeda',
        });
      }

      // Empresa
      if (this.currentFilters.companyId) {
        const companyLabel = this.getCompanyLabelById(this.currentFilters.companyId);
        activeFilterConfigs.push({
          propertyName: 'companyId',
          label: 'companyId',
          type: 'text',
          value: companyLabel,
          group: 'Empresa',
        });
      }

      // Tipo de Operação
      if (this.currentFilters.operationType) {
        const operationLabel = this.getOperationLabelById(this.currentFilters.operationType);
        activeFilterConfigs.push({
          propertyName: 'operationType',
          label: 'operationType',
          type: 'text',
          value: operationLabel,
          group: 'Tipo de Operação',
        });
      }

      // Data Inicial
      if (this.currentFilters.dateFrom) {
        activeFilterConfigs.push({
          propertyName: 'dateFrom',
          label: 'dateFrom',
          type: 'date',
          value: this.currentFilters.dateFrom,
          group: 'Data Inicial',
        });
      }

      // Data Final
      if (this.currentFilters.dateTo) {
        activeFilterConfigs.push({
          propertyName: 'dateTo',
          label: 'dateTo',
          type: 'date',
          value: this.currentFilters.dateTo,
          group: 'Data Final',
        });
      }

      // Referência
      if (this.currentFilters.reference) {
        activeFilterConfigs.push({
          propertyName: 'reference',
          label: 'reference',
          type: 'text',
          value: this.currentFilters.reference,
          group: 'Referência',
        });
      }

      // Valor Mínimo e Máximo (agrupados)
      if (this.currentFilters.notionalAmountFrom || this.currentFilters.notionalAmountTo) {
        const valueRange = this.formatRange(
          parseFloat(this.currentFilters.notionalAmountFrom || '0'),
          parseFloat(this.currentFilters.notionalAmountTo || '0')
        );
        activeFilterConfigs.push({
          propertyName: 'notionalAmountFrom',
          label: 'valueRange',
          type: 'text',
          value: valueRange,
          group: 'Faixa de Valor',
        });
      }

      // Operações Contratadas
      if (this.currentFilters.withHiredOperations) {
        activeFilterConfigs.push({
          propertyName: 'withHiredOperations',
          label: 'withHiredOperations',
          type: 'text',
          value: this.t(this.tk.Management.ContractedOperations),
          group: 'Operações Contratadas',
        });
      }

      // Operações Ativas e Abertas
      if (this.currentFilters.withHiredOperationsOpen) {
        activeFilterConfigs.push({
          propertyName: 'withHiredOperationsOpen',
          label: 'withHiredOperationsOpen',
          type: 'text',
          value: this.t(this.tk.Management.ContractsActiveAndOpen),
          group: 'Operações Ativas e Abertas',
        });
      }
    }

    this.sendActiveFilters.emit(activeFilterConfigs);
  }

  /**
   * Formatar range de valores monetários
   */
  private formatRange(min?: number, max?: number): string {
    if (min && max && min !== max) {
      return `R$ ${min.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} - R$ ${max.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    } else if (min) {
      return `A partir de R$ ${min.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    } else if (max) {
      return `Até R$ ${max.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    }
    return '';
  }

  /**
   * Métodos para obter labels pelos IDs usando os dados em cache
   */
  private getCurrencyLabelById(id: string): string {
    const option = this.currenciesCache.find(o => o.value === id);
    return option ? option.label : `Moeda ${id}`;
  }

  private getCompanyLabelById(id: string): string {
    const option = this.companiesCache.find(o => o.value === id);
    return option ? option.label : `Empresa ${id}`;
  }

  private getOperationLabelById(value: string): string {
    const option = this.operationOptions.find(o => o.value === value);
    return option ? option.label : value;
  }

  // Getters para compatibilidade com template existente
  get dateFrom(): string {
    return this.filterForm?.get('dateFrom')?.value || '';
  }

  get dateTo(): string {
    return this.filterForm?.get('dateTo')?.value || '';
  }
}
